import React, { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { LinkedIn, FileDownload } from '@mui/icons-material';
import { Statut, Effectif, ZoneGeographique, CRMFilters } from '../types/crm';
import { getLeadsByUserId } from '../lib/supabase/leadsService';
import { useAuth } from '../lib/supabase/AuthContext';
import { Database } from '../types/supabase';

type Lead = Database['public']['Tables']['leads']['Row'];

const statuts: Statut[] = ['Pas encore contacté', 'Contacté'];
const effectifs: Effectif[] = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5000+'];
const zonesGeographiques: ZoneGeographique[] = ['France', 'Europe', 'Amérique du Nord', 'Amérique du Sud', 'Asie', 'Afrique', 'Océanie'];

// Fonction utilitaire pour convertir un Lead de Supabase au format attendu pour l'affichage dans le CRM
const leadToCRMContact = (lead: Lead) => {
  return {
    id: lead.id,
    statut: lead.prospection_etape as Statut, 
    prenom: lead.prenom,
    nom: lead.nom,
    email: lead.email || '',
    phone: lead.telephone || '',
    linkedin: lead.linkedin || '',
    entreprise: lead.entreprise,
    jobTitle: lead.job_title || '',
    secteur: lead.secteur || '',
    effectif: lead.effectif as Effectif || '1-10',
    zoneGeographique: lead.zone as ZoneGeographique || 'France',
    estQualifie: lead.etat_rdv,
    dateCreation: lead.created_at,
    dateMiseAJour: lead.updated_at,
    campaignId: lead.campaign_id
  };
};

const CRMPage: React.FC = () => {
  const { user } = useAuth();
  // États
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [filters, setFilters] = useState<CRMFilters>({
    statut: 'tous',
    jobTitle: '',
    secteur: '',
    effectif: 'tous',
    zoneGeographique: 'tous',
    qualification: 'tous',
  });

  // Récupération des leads depuis Supabase
  useEffect(() => {
    const fetchLeads = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const { data, error } = await getLeadsByUserId(user.id);
        
        if (error) {
          throw error;
        }
        
        setLeads(data || []);
      } catch (err: any) {
        console.error('Erreur lors du chargement des leads:', err);
        setError(`Une erreur est survenue: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLeads();
  }, [user]);

  // Conversion des leads en format CRM pour l'affichage
  const contacts = useMemo(() => {
    // Filtrer pour n'afficher que les leads qualifiés
    const leadsQualifies = leads.filter(lead => lead.lead_qualifie === true);
    return leadsQualifies.map(leadToCRMContact);
  }, [leads]);

  // Filtrage des contacts
  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      if (filters.statut !== 'tous' && contact.statut !== filters.statut) return false;
      if (filters.jobTitle && contact.jobTitle !== filters.jobTitle) return false;
      if (filters.secteur && contact.secteur !== filters.secteur) return false;
      if (filters.effectif !== 'tous' && contact.effectif !== filters.effectif) return false;
      if (filters.zoneGeographique !== 'tous' && contact.zoneGeographique !== filters.zoneGeographique) return false;
      
      return true;
    });
  }, [contacts, filters]);

  // Gestion des sélections
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedContacts(filteredContacts.map(contact => contact.id));
    } else {
      setSelectedContacts([]);
    }
  };

  const handleSelectContact = (contactId: string) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  // Gestion des filtres
  const handleFilterChange = (key: keyof CRMFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  // Fonction pour télécharger les leads sélectionnés en CSV
  const handleDownloadCSV = () => {
    if (selectedContacts.length === 0) return;
    
    const selectedLeadsData = filteredContacts.filter(contact => 
      selectedContacts.includes(contact.id)
    );
    
    // En-têtes CSV
    const headers = [
      'Statut',
      'Prénom', 
      'Nom',
      'Email',
      'Téléphone',
      'Entreprise',
      'Job Title',
      'LinkedIn',
      'Secteur',
      'Effectif',
      'Zone géographique',
      'Date de création',
      'Date de mise à jour'
    ];
    
    // Conversion des données en format CSV
    const csvContent = [
      headers.join(','),
      ...selectedLeadsData.map(contact => [
        `"${contact.statut}"`,
        `"${contact.prenom}"`,
        `"${contact.nom}"`,
        `"${contact.email}"`,
        `"${contact.phone}"`,
        `"${contact.entreprise}"`,
        `"${contact.jobTitle}"`,
        `"${contact.linkedin}"`,
        `"${contact.secteur}"`,
        `"${contact.effectif}"`,
        `"${contact.zoneGeographique}"`,
        `"${contact.dateCreation ? new Date(contact.dateCreation).toLocaleDateString('fr-FR') : 'Non définie'}"`,
        `"${contact.dateMiseAJour ? new Date(contact.dateMiseAJour).toLocaleDateString('fr-FR') : 'Non définie'}"`
      ].join(','))
    ].join('\n');
    
    // Création et téléchargement du fichier
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `leads_qualifies_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Extraction des valeurs uniques pour les sélecteurs
  const uniqueJobTitles = useMemo(() => {
    return Array.from(new Set(contacts.map(contact => contact.jobTitle)))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'fr'));
  }, [contacts]);

  const uniqueSecteurs = useMemo(() => {
    return Array.from(new Set(contacts.map(contact => contact.secteur)))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'fr'));
  }, [contacts]);

  const uniqueEffectifs = useMemo(() => {
    return Array.from(new Set(contacts.map(contact => contact.effectif)))
      .filter(Boolean)
      .sort((a, b) => {
        // Extraction des nombres pour comparaison
        const [minA] = a.split('-').map(Number);
        const [minB] = b.split('-').map(Number);
        return minA - minB;
      });
  }, [contacts]);

  if (loading && leads.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        {/* En-tête */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            CRM
          </Typography>
        </Box>
        
        {/* Message d'erreur */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Filtres */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={filters.statut}
              label="Statut"
              onChange={(e) => handleFilterChange('statut', e.target.value)}
            >
              <MenuItem value="tous">Tous les statuts</MenuItem>
              {statuts.map(statut => (
                <MenuItem key={statut} value={statut}>{statut}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Job Title</InputLabel>
            <Select
              value={filters.jobTitle}
              label="Job Title"
              onChange={(e) => handleFilterChange('jobTitle', e.target.value)}
            >
              <MenuItem value="">Tous les job titles</MenuItem>
              {uniqueJobTitles.map(jobTitle => (
                <MenuItem key={jobTitle} value={jobTitle}>{jobTitle}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Secteur</InputLabel>
            <Select
              value={filters.secteur}
              label="Secteur"
              onChange={(e) => handleFilterChange('secteur', e.target.value)}
            >
              <MenuItem value="">Tous les secteurs</MenuItem>
              {uniqueSecteurs.map(secteur => (
                <MenuItem key={secteur} value={secteur}>{secteur}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Effectif</InputLabel>
            <Select
              value={filters.effectif}
              label="Effectif"
              onChange={(e) => handleFilterChange('effectif', e.target.value)}
            >
              <MenuItem value="tous">Tous les effectifs</MenuItem>
              {uniqueEffectifs.map(effectif => (
                <MenuItem key={effectif} value={effectif}>{effectif}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Zone géographique</InputLabel>
            <Select
              value={filters.zoneGeographique}
              label="Zone géographique"
              onChange={(e) => handleFilterChange('zoneGeographique', e.target.value)}
            >
              <MenuItem value="tous">Toutes les zones</MenuItem>
              {zonesGeographiques.map(zone => (
                <MenuItem key={zone} value={zone}>{zone}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Actions pour la sélection */}
        {selectedContacts.length > 0 && (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">
                {selectedContacts.length} lead(s) sélectionné(s)
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileDownload />}
                onClick={handleDownloadCSV}
                disabled={loading}
              >
                Télécharger CSV
              </Button>
            </Box>
          </Box>
        )}

        {/* Tableau */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedContacts.length === filteredContacts.length && filteredContacts.length > 0}
                    indeterminate={selectedContacts.length > 0 && selectedContacts.length < filteredContacts.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Prénom</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell>Entreprise</TableCell>
                <TableCell>Job Title</TableCell>
                <TableCell>LinkedIn</TableCell>
                <TableCell>Secteur</TableCell>
                <TableCell>Effectif</TableCell>
                <TableCell>Zone géographique</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredContacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body1" color="textSecondary" sx={{ py: 3 }}>
                      Aucun lead qualifié trouvé pour les filtres sélectionnés
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredContacts.map((contact) => (
                  <TableRow key={contact.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedContacts.includes(contact.id)}
                      onChange={() => handleSelectContact(contact.id)}
                    />
                  </TableCell>
                  <TableCell>
                      <Chip 
                        label={contact.statut} 
                        color="primary" 
                        size="small" 
                      />
                  </TableCell>
                  <TableCell>{contact.prenom}</TableCell>
                  <TableCell>{contact.nom}</TableCell>
                  <TableCell>{contact.email}</TableCell>
                  <TableCell>{contact.phone}</TableCell>
                  <TableCell>{contact.entreprise}</TableCell>
                  <TableCell>{contact.jobTitle}</TableCell>
                  <TableCell>
                      {contact.linkedin ? (
                    <IconButton
                      href={contact.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      color="primary"
                    >
                      <LinkedIn />
                    </IconButton>
                      ) : (
                        '-'
                      )}
                  </TableCell>
                  <TableCell>{contact.secteur}</TableCell>
                  <TableCell>{contact.effectif}</TableCell>
                  <TableCell>{contact.zoneGeographique}</TableCell>
                </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default CRMPage; 